import React, { useState, useEffect, useMemo } from 'react';
import { studentService, brandingService } from '../../../services/index.js';
import { API_BASE_URL } from '../../../config.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  Ticket, Printer, Download, Eye, Search, 
  RefreshCw, X, User, BookOpen, Award
} from 'lucide-react';

// ==================== HELPER UTILITIES ====================

const cleanStr = (s) => (s || '').toString().toLowerCase().trim();
const cleanSec = (s) => cleanStr(s).replace(/^section\s*/i, '').trim();

const getStudentClass = (s) => {
  if (!s) return '';
  return s.className || s.academicLevel || s.studentClass || s.class || s.degree || s.standard || s.currentClass || '';
};

const normalizeClassName = (name) => {
  if (!name) return '';
  return name
    .toString()
    .toLowerCase()
    .replace(/^class\s*/i, '')
    .replace(/\s*standard$/i, '')
    .replace(/\s*std$/i, '')
    .replace(/(st|nd|rd|th)$/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

const matchClassNames = (classA, classB) => {
  if (!classA && !classB) return true;
  if (!classA || !classB) return false;
  const a = cleanStr(classA);
  const b = cleanStr(classB);
  if (a === b) return true;
  const normA = normalizeClassName(a);
  const normB = normalizeClassName(b);
  return normA.length > 0 && normA === normB;
};

const matchSection = (studentSec, filterSec) => {
  if (!filterSec || filterSec === 'ALL') return true;
  const normS = cleanSec(studentSec);
  const normF = cleanSec(filterSec);
  if (!normS && normF === 'all') return true;
  return normS === normF;
};

const normalizeAcademicYear = (year) => {
  if (!year) return '';
  const match = year.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const century = match[1].slice(0, 2);
    return `${match[1]}-${century}${match[2]}`;
  }
  return year;
};

const resolveStudentPhotoUrl = (student) => {
  if (!student) return null;
  const photo = student.photo || student.photoUrl || student.studentPhoto || student.image || student.imageUrl;
  if (!photo || typeof photo !== 'string' || photo.trim() === '') return null;
  if (photo.startsWith('data:') || photo.startsWith('blob:') || photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  const cleanPath = photo.startsWith('/') ? photo : `/${photo}`;
  return `${API_BASE_URL}${cleanPath}`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr || dateStr === 'N/A' || dateStr === '—') return '—';
  try {
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const monthIdx = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      if (day >= 1 && day <= 31 && monthIdx >= 0 && monthIdx < 12) {
        return `${day < 10 ? '0' + day : day}-${monthNames[monthIdx]}-${year}`;
      }
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
};

const getDayName = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  } catch (e) {
    return '—';
  }
};

const isOfficialExam = (t) => {
  if (!t) return false;
  if (t.createdByRole === 'TEACHER' && t.teacherId && t.teacherId !== 'ADMIN' && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM') {
    return false;
  }
  return true;
};

/**
 * Formats a time string into 12-hour AM/PM format
 * Supports "09:00", "09:00 AM", "14:30" -> "02:30 PM", etc.
 */
const formatTimeStr = (tStr) => {
  if (!tStr || typeof tStr !== 'string') return '';
  const trimmed = tStr.trim();
  if (!trimmed) return '';
  // 12-hour format with AM/PM
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampmMatch) {
    const hh = parseInt(ampmMatch[1], 10);
    const mm = ampmMatch[2];
    const ampm = ampmMatch[3].toUpperCase();
    return `${hh < 10 ? '0' + hh : hh}:${mm} ${ampm}`;
  }
  // 24-hour format HH:mm or HH:mm:ss
  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hours = parseInt(match24[1], 10);
    const minutes = match24[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const padHours = hours < 10 ? `0${hours}` : `${hours}`;
    return `${padHours}:${minutes} ${ampm}`;
  }
  return trimmed;
};

/**
 * Extracts session name if genuine session data exists in row.session or instructions.
 * If instructions is merely generic text ("Official School Examination"), returns null.
 */
const extractSessionName = (row) => {
  if (!row) return null;
  const directSession = (row.session || row.sessionName || '').trim();
  if (directSession && directSession.length > 0 && directSession !== '—') {
    return directSession;
  }
  const instr = (row.instructions || '').trim();
  if (!instr) return null;
  const sessionMatch = instr.match(/\b(morning\s*session|afternoon\s*session|evening\s*session|forenoon\s*session|fn\s*session|an\s*session)\b/i);
  if (sessionMatch) {
    return sessionMatch[1].replace(/\b\w/g, l => l.toUpperCase());
  }
  if (/^(morning|afternoon|evening|forenoon)$/i.test(instr)) {
    return `${instr.charAt(0).toUpperCase() + instr.slice(1).toLowerCase()} Session`;
  }
  return null;
};

/**
 * Resolves the timing presentation strictly according to:
 * - Rule 1: If actual start & end times exist -> "09:00 AM - 09:30 AM"
 * - If only start time exists -> "09:00 AM"
 * - Rule 2: If only genuine session exists -> "Morning Session"
 * - If neither exists -> "—" (NEVER hardcodes "Morning Session")
 */
const resolveExamTiming = (row) => {
  const s = formatTimeStr(row?.startTime);
  const e = formatTimeStr(row?.endTime);
  if (s && e) {
    return `${s} - ${e}`;
  }
  if (s) {
    return s;
  }
  const session = extractSessionName(row);
  if (session) {
    return session;
  }
  return '—';
};

/**
 * Converts a time string (e.g. "09:00 AM", "09:00") into minutes from midnight.
 */
const parseTimeToMinutes = (tStr) => {
  if (!tStr || typeof tStr !== 'string') return null;
  const trimmed = tStr.trim();
  if (!trimmed) return null;

  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hh = parseInt(ampmMatch[1], 10);
    const mm = parseInt(ampmMatch[2], 10);
    const ampm = ampmMatch[3].toUpperCase();
    if (hh === 12) hh = (ampm === 'AM' ? 0 : 12);
    else if (ampm === 'PM') hh += 12;
    return hh * 60 + mm;
  }

  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    const hh = parseInt(match24[1], 10);
    const mm = parseInt(match24[2], 10);
    return hh * 60 + mm;
  }

  return null;
};

/**
 * Converts minutes from midnight back to 12-hour "HH:MM AM/PM"
 */
const minutesToDisplayTime = (totalMinutes) => {
  if (totalMinutes === null || isNaN(totalMinutes)) return '';
  let normalized = (totalMinutes + 1440) % 1440;
  let hh = Math.floor(normalized / 60);
  const mm = normalized % 60;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12 || 12;
  const padHH = hh < 10 ? `0${hh}` : `${hh}`;
  const padMM = mm < 10 ? `0${mm}` : `${mm}`;
  return `${padHH}:${padMM} ${ampm}`;
};

/**
 * Calculates reporting time from earliest paper start time in exam timetable.
 * Subtracts 15 minutes by default.
 * Returns null if no paper has an actual start time.
 */
const deriveReportingTime = (timetable, minutesPrior = 15) => {
  if (!timetable || timetable.length === 0) return null;
  const validTimes = timetable
    .map(row => parseTimeToMinutes(row.startTime))
    .filter(m => m !== null);

  if (validTimes.length === 0) return null;

  const earliestMinutes = Math.min(...validTimes);
  const reportingMinutes = earliestMinutes - minutesPrior;
  return minutesToDisplayTime(reportingMinutes);
};

// ==================== PRINTABLE HALL TICKET CARD ====================

const HallTicketCard = ({
  student,
  examTitle,
  academicYear,
  timetable = [],
  orgData = {},
  brandingData = {},
  examCenter = '',
  reportingTime = '',
  customInstructions = []
}) => {
  const schoolName = orgData?.orgName || orgData?.name || orgData?.institutionName || localStorage.getItem('orgName') || 'School Examination Portal';
  const schoolAddress = orgData?.address || orgData?.contactAddress || '';
  const schoolPhone = orgData?.phone || orgData?.contactPhone || '';
  const schoolEmail = orgData?.email || orgData?.contactEmail || '';
  const affiliation = orgData?.affiliation || orgData?.affiliationNumber || orgData?.schoolCode || orgData?.board || '';

  const logoUrl = brandingData?.logoUrl || orgData?.logo || orgData?.logoUrl || '';
  const sealUrl = brandingData?.sealUrl || orgData?.sealUrl || '';
  const signatureUrl = brandingData?.signatureUrl || orgData?.signatureUrl || '';

  const photoSrc = resolveStudentPhotoUrl(student);

  const studentName = student?.name || student?.studentName || student?.fullName || 'Student Name';
  const rollNo = student?.rollNo || student?.rollNumber || student?.admissionNo || student?.studentId || '—';
  const admissionNo = student?.admissionNo || student?.studentId || '—';
  const studentClass = getStudentClass(student) || '—';
  const studentSection = student?.section || student?.sectionName || '—';
  const dob = formatDisplayDate(student?.dob || student?.dateOfBirth);
  const gender = student?.gender || '—';
  const parentName = student?.fatherName || student?.parentName || student?.guardianName || student?.motherName || '—';
  const resolvedCenter = examCenter || (schoolName && schoolName !== 'School Examination Portal' ? `${schoolName} Campus` : 'School Examination Hall');
  
  // Rule 8: Derive reporting time only when real value is available; no fabricated times
  const cleanReportingTime = (reportingTime || '').trim();
  const hasValidReportingTime = cleanReportingTime !== '' && cleanReportingTime !== '—';

  const defaultInstructions = [
    'Candidates must bring this original Hall Ticket and their School Identity Card to the examination hall for every session.',
    hasValidReportingTime
      ? `Candidates must report to the examination hall at least 15 minutes prior to commencement (${cleanReportingTime}).`
      : 'Candidates must report to the examination hall at least 15 minutes prior to the commencement of each paper.',
    'No candidate will be allowed to leave the examination hall before the first hour of the examination has elapsed.',
    'Electronic devices, mobile phones, smartwatches, calculators, and unauthorized papers are strictly prohibited.',
    'Candidates must write their Name, Roll Number, and Subject clearly on the answer booklet.',
    'Maintain absolute silence and examination discipline throughout the examination session.'
  ];

  const instructions = customInstructions && customInstructions.length > 0 ? customInstructions : defaultInstructions;

  return (
    <div
      className="printable-hall-ticket hall-ticket-card-page"
      style={{
        width: '100%',
        maxWidth: '190mm',
        minHeight: '265mm',
        margin: '0 auto',
        padding: '14mm 12mm',
        boxSizing: 'border-box',
        background: '#ffffff',
        color: '#0f172a',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        border: '2.5px solid #1e3a8a',
        borderRadius: '8px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
      }}
    >
      <div>
        {/* 1. School Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '2px solid #1e3a8a',
          paddingBottom: '10px',
          gap: '12px'
        }}>
          {logoUrl ? (
            <img
              src={logoUrl.startsWith('data:') || logoUrl.startsWith('http') ? logoUrl : `${API_BASE_URL}${logoUrl}`}
              alt="School Logo"
              style={{ width: '64px', height: '64px', objectFit: 'contain', flexShrink: 0 }}
              crossOrigin="anonymous"
            />
          ) : (
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '8px',
              background: '#eff6ff',
              border: '1.5px solid #bfdbfe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Award size={32} color="#1e3a8a" />
            </div>
          )}

          <div style={{ flex: 1, textAlign: 'center' }}>
            <h1 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '900',
              color: '#1e3a8a',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              {schoolName}
            </h1>
            {affiliation && (
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#b45309', marginTop: '2px' }}>
                {affiliation}
              </div>
            )}
            {schoolAddress && (
              <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                {schoolAddress}
              </div>
            )}
            {(schoolPhone || schoolEmail) && (
              <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '1px' }}>
                {schoolPhone ? `Tel: ${schoolPhone}` : ''}
                {schoolPhone && schoolEmail ? ' | ' : ''}
                {schoolEmail ? `Email: ${schoolEmail}` : ''}
              </div>
            )}
          </div>

          {sealUrl ? (
            <img
              src={sealUrl.startsWith('data:') || sealUrl.startsWith('http') ? sealUrl : `${API_BASE_URL}${sealUrl}`}
              alt="Seal"
              style={{ width: '60px', height: '60px', objectFit: 'contain', flexShrink: 0, opacity: 0.9 }}
              crossOrigin="anonymous"
            />
          ) : (
            <div style={{ width: '60px', height: '60px', flexShrink: 0 }} />
          )}
        </div>

        {/* 2. Official Admit Card Title Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          color: '#ffffff',
          textAlign: 'center',
          padding: '6px 12px',
          borderRadius: '4px',
          marginTop: '10px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '900', letterSpacing: '1px', textTransform: 'uppercase' }}>
            EXAMINATION HALL TICKET / ADMIT CARD
          </div>
          <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#93c5fd', marginTop: '1px' }}>
            {examTitle ? `${examTitle.toUpperCase()} EXAMINATION` : 'ANNUAL EXAMINATION'} — ACADEMIC YEAR: {academicYear || '2026-2027'}
          </div>
        </div>

        {/* 3. Student Identification Area & Photo */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginTop: '12px',
          padding: '10px 14px',
          background: '#f8fafc',
          border: '1.5px solid #cbd5e1',
          borderRadius: '6px'
        }}>
          {/* Details Table */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '12px' }}>
            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Candidate Name:</span>
              <span style={{ fontWeight: '800', color: '#0f172a', textTransform: 'uppercase' }}>{studentName}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Roll Number:</span>
              <span style={{ fontWeight: '800', color: '#1e3a8a' }}>{rollNo}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Admission No:</span>
              <span style={{ fontWeight: '700', color: '#0f172a' }}>{admissionNo}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Class & Section:</span>
              <span style={{ fontWeight: '800', color: '#0f172a' }}>
                {studentClass} {studentSection && studentSection !== 'ALL' ? `(Sec ${studentSection})` : ''}
              </span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Date of Birth:</span>
              <span style={{ fontWeight: '600', color: '#1e293b' }}>{dob}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Gender:</span>
              <span style={{ fontWeight: '600', color: '#1e293b' }}>{gender}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Parent/Guardian:</span>
              <span style={{ fontWeight: '600', color: '#1e293b' }}>{parentName}</span>
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Reporting Time:</span>
              <span style={{ fontWeight: '700', color: hasValidReportingTime ? '#b45309' : '#64748b' }}>
                {hasValidReportingTime ? cleanReportingTime : '—'}
              </span>
            </div>

            <div style={{ display: 'flex', gridColumn: 'span 2' }}>
              <span style={{ width: '115px', fontWeight: '700', color: '#475569' }}>Exam Center:</span>
              <span style={{ fontWeight: '700', color: '#0f172a' }}>{resolvedCenter}</span>
            </div>
          </div>

          {/* Student Photo */}
          <div style={{
            width: '92px',
            height: '112px',
            border: '1.5px solid #0f172a',
            borderRadius: '4px',
            overflow: 'hidden',
            background: '#f1f5f9',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            textAlign: 'center',
            padding: '2px'
          }}>
            {photoSrc ? (
              <img
                src={photoSrc}
                alt="Student"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                crossOrigin="anonymous"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <User size={28} color="#94a3b8" />
                <span style={{ fontSize: '8.5px', fontWeight: '800', color: '#64748b', lineHeight: '1.1', textTransform: 'uppercase' }}>
                  Affix Passport Photo
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 4. Examination Timetable Table */}
        <div style={{ marginTop: '12px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}>
            <span style={{ fontSize: '11.5px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Official Examination Schedule
            </span>
            <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '600' }}>
              Total Papers: {timetable.length}
            </span>
          </div>

          {timetable.length === 0 ? (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              border: '1px dashed #cbd5e1',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#dc2626',
              fontWeight: '600',
              background: '#fef2f2'
            }}>
              No exam schedule has been configured for this examination.
            </div>
          ) : (() => {
            // Rule 7: Dynamic column header (Time vs Session vs Time / Session)
            const hasAnyActualTime = timetable.some(r => Boolean(r.startTime && r.startTime.trim() !== ''));
            const hasAnySession = timetable.some(r => Boolean(extractSessionName(r)));
            const timeColumnHeader = (hasAnyActualTime && hasAnySession)
              ? 'Time / Session'
              : (!hasAnyActualTime && hasAnySession)
              ? 'Session'
              : 'Time';

            return (
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11.5px',
                border: '1.5px solid #0f172a'
              }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', color: '#0f172a', borderBottom: '1.5px solid #0f172a' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '38px', borderRight: '1px solid #cbd5e1' }}>Sl</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', borderRight: '1px solid #cbd5e1' }}>Subject</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '90px', borderRight: '1px solid #cbd5e1' }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '60px', borderRight: '1px solid #cbd5e1' }}>Day</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '135px', borderRight: '1px solid #cbd5e1' }}>{timeColumnHeader}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '65px', borderRight: '1px solid #cbd5e1' }}>Max Marks</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '95px', borderRight: '1px solid #cbd5e1' }}>Candidate Sign</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: '95px' }}>Invigilator Sign</th>
                  </tr>
                </thead>
                <tbody>
                  {timetable.map((row, idx) => {
                    const timeText = resolveExamTiming(row);
                    return (
                      <tr key={row.testId || idx} style={{ borderBottom: '1px solid #cbd5e1', background: idx % 2 === 1 ? '#fafafa' : '#ffffff' }}>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#64748b', borderRight: '1px solid #cbd5e1' }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: '6px 10px', fontWeight: '800', color: '#0f172a', borderRight: '1px solid #cbd5e1' }}>
                          {row.subjectName}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#1e293b', borderRight: '1px solid #cbd5e1' }}>
                          {formatDisplayDate(row.testDate)}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#475569', borderRight: '1px solid #cbd5e1' }}>
                          {getDayName(row.testDate)}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '600', color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                          {timeText}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#0f172a', borderRight: '1px solid #cbd5e1' }}>
                          {row.maxMarks || '—'}
                        </td>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #cbd5e1' }}></td>
                        <td style={{ padding: '6px 8px' }}></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>

        {/* 5. Instructions Box */}
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          border: '1px solid #94a3b8',
          borderRadius: '5px',
          background: '#f8fafc'
        }}>
          <div style={{ fontSize: '10.5px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', marginBottom: '4px' }}>
            Important Instructions for Candidates
          </div>
          <ol style={{ margin: 0, paddingLeft: '16px', fontSize: '10px', color: '#334155', lineHeight: '1.4' }}>
            {instructions.map((inst, i) => (
              <li key={i} style={{ marginBottom: '2px' }}>{inst}</li>
            ))}
          </ol>
        </div>
      </div>

      {/* 6. Signature Footers */}
      <div style={{
        marginTop: '16px',
        paddingTop: '10px',
        borderTop: '1.5px solid #0f172a',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        textAlign: 'center',
        gap: '12px',
        alignItems: 'flex-end'
      }}>
        {/* Candidate Signature */}
        <div>
          <div style={{ height: '36px' }}></div>
          <div style={{ borderTop: '1px dashed #64748b', margin: '0 16px', paddingTop: '4px', fontSize: '10.5px', fontWeight: '700', color: '#0f172a' }}>
            Candidate's Signature
          </div>
        </div>

        {/* Class Teacher Signature */}
        <div>
          <div style={{ height: '36px' }}></div>
          <div style={{ borderTop: '1px dashed #64748b', margin: '0 16px', paddingTop: '4px', fontSize: '10.5px', fontWeight: '700', color: '#0f172a' }}>
            Class Teacher
          </div>
        </div>

        {/* Principal / Controller */}
        <div>
          <div style={{ height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {signatureUrl ? (
              <img
                src={signatureUrl.startsWith('data:') || signatureUrl.startsWith('http') ? signatureUrl : `${API_BASE_URL}${signatureUrl}`}
                alt="Signature"
                style={{ maxHeight: '34px', maxWidth: '120px', objectFit: 'contain' }}
                crossOrigin="anonymous"
              />
            ) : null}
          </div>
          <div style={{ borderTop: '1px dashed #64748b', margin: '0 16px', paddingTop: '4px', fontSize: '10.5px', fontWeight: '800', color: '#1e3a8a' }}>
            Principal / Controller of Exams
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const HallTicketGenerator = ({
  orgData,
  students: propStudents = [],
  classes = [],
  tests = [],
  activeAcademicYear,
  availableAcademicYears = [],
  examTypeConfigs = [],
  preselect = null,
  onOpenExamSetup,
  showSuccessNotification,
  showErrorNotification
}) => {
  // 1. Initial State
  const resolveInitialAcademicYear = () => {
    if (preselect?.academicYear) return preselect.academicYear;
    if (activeAcademicYear) return activeAcademicYear;
    if (orgData?.academicYear) return orgData.academicYear;
    if (orgData?.activeAcademicYear) return orgData.activeAcademicYear;
    const stored = localStorage.getItem('academicYear') || localStorage.getItem('activeAcademicYear');
    if (stored) return stored;
    const d = new Date();
    const y = d.getFullYear();
    const start = d.getMonth() >= 4 ? y : y - 1;
    return `${start}-${(start + 1).toString().slice(-2)}`;
  };

  const [academicYear, setAcademicYear] = useState(resolveInitialAcademicYear);
  const [selectedExamTitle, setSelectedExamTitle] = useState(preselect?.examTitle || '');
  const [selectedClass, setSelectedClass] = useState(preselect?.className || '');
  const [selectedSection, setSelectedSection] = useState(preselect?.section || 'ALL');

  const [allStudents, setAllStudents] = useState(propStudents || []);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [brandingData, setBrandingData] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [previewStudent, setPreviewStudent] = useState(null);

  const [examCenter, setExamCenter] = useState(
    orgData?.orgName ? `${orgData.orgName} Main Campus` : 'School Examination Hall'
  );
  // Rule 3 & 8: Dynamic reporting time state; no hardcoded 08:45 AM
  const [customReportingTime, setCustomReportingTime] = useState('');
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, msg: '' });

  // Sync with prop updates
  useEffect(() => {
    if (activeAcademicYear && !preselect?.academicYear) {
      setAcademicYear(activeAcademicYear);
    }
  }, [activeAcademicYear, preselect?.academicYear]);

  // Load students self-contained if prop is empty
  useEffect(() => {
    if (!propStudents || propStudents.length === 0) {
      let resolvedOrgId = orgData?.orgId || localStorage.getItem('orgId');
      if (!resolvedOrgId) {
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const u = JSON.parse(userStr);
            resolvedOrgId = u.orgId || u.organizationId;
          }
        } catch (e) {}
      }
      setLoadingStudents(true);
      studentService.getStudents(resolvedOrgId)
        .then(res => {
          if (Array.isArray(res)) setAllStudents(res);
          else if (res && Array.isArray(res.students)) setAllStudents(res.students);
          else if (res && Array.isArray(res.data)) setAllStudents(res.data);
        })
        .catch(err => console.error('Failed to load students in HallTicketGenerator:', err))
        .finally(() => setLoadingStudents(false));
    } else {
      setAllStudents(propStudents);
    }
  }, [propStudents, orgData?.orgId]);

  // Fetch school branding info (logo, signature, seal)
  useEffect(() => {
    brandingService.getBranding()
      .then(res => {
        if (res && res.success && res.branding) {
          setBrandingData(res.branding);
        }
      })
      .catch(err => console.error('Failed to fetch branding for hall tickets:', err));
  }, []);

  // Update preselect values if prop changes
  useEffect(() => {
    if (preselect) {
      if (preselect.academicYear) setAcademicYear(preselect.academicYear);
      if (preselect.examTitle) setSelectedExamTitle(preselect.examTitle);
      if (preselect.className) setSelectedClass(preselect.className);
      if (preselect.section) setSelectedSection(preselect.section);
    }
  }, [preselect]);

  // 2. Computed Options
  const academicYearOptions = useMemo(() => {
    if (availableAcademicYears && availableAcademicYears.length > 0) {
      return availableAcademicYears;
    }
    const years = new Set();
    if (academicYear) years.add(academicYear);
    const curYear = new Date().getFullYear();
    for (let i = -2; i <= 2; i++) {
      const y = curYear + i;
      years.add(`${y}-${(y + 1).toString().slice(-2)}`);
    }
    return Array.from(years).sort().reverse();
  }, [availableAcademicYears, academicYear]);

  // Only official school exams matching the selected academic year
  const officialTestsForYear = useMemo(() => {
    const normActive = normalizeAcademicYear(academicYear);
    return tests.filter(isOfficialExam).filter(t => {
      const tYear = (t.description || t.academicYear || '').trim();
      if (tYear && /^\d{4}-\d{2,4}$/.test(tYear)) {
        return normalizeAcademicYear(tYear) === normActive;
      }
      return true;
    });
  }, [tests, academicYear]);

  // Available Official Exams for this Academic Year
  const availableExams = useMemo(() => {
    const titles = [...new Set(officialTestsForYear.map(t => t.title).filter(Boolean))];
    if (examTypeConfigs && examTypeConfigs.length > 0) {
      const orderMap = {};
      examTypeConfigs.forEach((et, idx) => { orderMap[et.examCode] = et.sortOrder || idx + 1; });
      titles.sort((a, b) => {
        const oa = orderMap[a] !== undefined ? orderMap[a] : 9999;
        const ob = orderMap[b] !== undefined ? orderMap[b] : 9999;
        return oa - ob;
      });
    }
    return titles;
  }, [officialTestsForYear, examTypeConfigs]);

  // Auto-select first exam if none selected
  useEffect(() => {
    if (availableExams.length > 0 && (!selectedExamTitle || !availableExams.includes(selectedExamTitle))) {
      setSelectedExamTitle(availableExams[0]);
    }
  }, [availableExams, selectedExamTitle]);

  // Classes available from DB or tests
  const availableClasses = useMemo(() => {
    if (classes && classes.length > 0) {
      const dbClasses = [...new Set(classes.map(c => c.className || c.name))].filter(Boolean);
      if (dbClasses.length > 0) return dbClasses;
    }
    const testClasses = [...new Set(officialTestsForYear.map(t => t.className).filter(Boolean))];
    return testClasses;
  }, [classes, officialTestsForYear]);

  // Auto-select first class if none selected
  useEffect(() => {
    if (availableClasses.length > 0 && (!selectedClass || !availableClasses.includes(selectedClass))) {
      setSelectedClass(availableClasses[0]);
    }
  }, [availableClasses, selectedClass]);

  // DB-driven sections for the selected class
  const getSectionsForClass = (clsName) => {
    if (!classes || classes.length === 0 || !clsName) return [];
    const normalized = (s) => (s || '').toString().toLowerCase().trim();
    const relevant = classes.filter(c => normalized(c.className || c.name) === normalized(clsName));
    const unique = [...new Set(relevant.map(c => c.section || c.sectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return unique;
  };

  const availableSections = useMemo(() => {
    const dbSecs = getSectionsForClass(selectedClass);
    if (dbSecs.length > 0) return dbSecs;
    // Fallback to sections found in tests for this class
    const tSecs = [...new Set(officialTestsForYear
      .filter(t => matchClassNames(t.className, selectedClass))
      .map(t => t.section)
      .filter(s => s && s !== 'ALL'))];
    return tSecs;
  }, [classes, selectedClass, officialTestsForYear]);

  // 3. Exact Exam Timetable for Selected Exam + Class + Section
  const examTimetable = useMemo(() => {
    if (!selectedExamTitle || !selectedClass) return [];

    const matching = officialTestsForYear.filter(t => {
      if (t.title !== selectedExamTitle) return false;
      if (!matchClassNames(t.className, selectedClass)) return false;
      return matchSection(t.section, selectedSection);
    });

    const map = new Map();
    matching.forEach(t => {
      const sName = t.subjectName || 'Subject';
      // Deduplicate key combines subject, testDate, and startTime so separate papers on the same date are preserved
      const key = `${sName}_${t.testDate || ''}_${t.startTime || ''}`;
      if (!map.has(key)) {
        map.set(key, {
          testId: t.testId,
          subjectName: sName,
          testDate: t.testDate || '',
          startTime: t.startTime || '',
          endTime: t.endTime || '',
          session: t.session || '',
          instructions: t.instructions || '',
          maxMarks: t.maxMarks || 80,
          durationMinutes: t.durationMinutes
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const dCmp = (a.testDate || '').localeCompare(b.testDate || '');
      if (dCmp !== 0) return dCmp;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [officialTestsForYear, selectedExamTitle, selectedClass, selectedSection]);

  // Rule 3 & 8: Dynamically derive reporting time from earliest start time in timetable (15 mins prior)
  const autoReportingTime = useMemo(() => {
    return deriveReportingTime(examTimetable, 15);
  }, [examTimetable]);

  // Effective reporting time: user manual override > auto-derived > empty string (NEVER fabricated 08:45 AM)
  const effectiveReportingTime = useMemo(() => {
    if (customReportingTime && customReportingTime.trim() !== '') {
      return customReportingTime.trim();
    }
    if (autoReportingTime) {
      return `${autoReportingTime} (15 mins prior)`;
    }
    return '';
  }, [customReportingTime, autoReportingTime]);

  // 4. Eligible Students Filtered from DB
  const eligibleStudents = useMemo(() => {
    if (!selectedClass) return [];
    const normActive = normalizeAcademicYear(academicYear);

    return allStudents.filter(s => {
      // Tenant validation
      if (orgData?.orgId && s.orgId && s.orgId !== orgData.orgId) return false;

      // Class match
      const sClass = getStudentClass(s);
      if (!matchClassNames(sClass, selectedClass)) return false;

      // Section match
      if (!matchSection(s.section || s.sectionName, selectedSection)) return false;

      // Academic Year match if student record has admissionYear/academicYear
      const sYear = s.academicYear || s.admissionYear;
      if (sYear && academicYear && /^\d{4}-\d{2,4}$/.test(sYear.trim())) {
        if (normalizeAcademicYear(sYear) !== normActive) {
          // Keep if student is actively enrolled in current class
        }
      }

      return true;
    }).sort((a, b) => {
      const rollA = parseInt(a.rollNo || a.rollNumber || 999999, 10);
      const rollB = parseInt(b.rollNo || b.rollNumber || 999999, 10);
      if (!isNaN(rollA) && !isNaN(rollB) && rollA !== rollB) return rollA - rollB;
      return (a.name || a.studentName || '').localeCompare(b.name || b.studentName || '');
    });
  }, [allStudents, selectedClass, selectedSection, academicYear, orgData?.orgId]);

  // Filter students by search term
  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return eligibleStudents;
    const q = searchTerm.toLowerCase();
    return eligibleStudents.filter(s => {
      const name = (s.name || s.studentName || s.fullName || '').toLowerCase();
      const adm = (s.admissionNo || s.studentId || '').toLowerCase();
      const roll = (s.rollNo || s.rollNumber || '').toLowerCase();
      return name.includes(q) || adm.includes(q) || roll.includes(q);
    });
  }, [eligibleStudents, searchTerm]);

  // Select all eligible students by default when class/section/exam changes
  useEffect(() => {
    if (eligibleStudents.length > 0) {
      const allIds = new Set(eligibleStudents.map(s => s.studentId || s.id));
      setSelectedStudentIds(allIds);
    } else {
      setSelectedStudentIds(new Set());
    }
  }, [eligibleStudents]);

  // Selection toggle handlers
  const handleToggleStudent = (studentId) => {
    const next = new Set(selectedStudentIds);
    if (next.has(studentId)) {
      next.delete(studentId);
    } else {
      next.add(studentId);
    }
    setSelectedStudentIds(next);
  };

  const handleSelectAll = () => {
    const next = new Set(filteredStudents.map(s => s.studentId || s.id));
    setSelectedStudentIds(next);
  };

  const handleClearAll = () => {
    setSelectedStudentIds(new Set());
  };

  // 5. Actions: Print Single, Print Selected (Bulk), Download PDF Single, Download PDF Bulk
  const [printTargetStudent, setPrintTargetStudent] = useState(null);

  const handlePrintSingle = (student) => {
    setPrintTargetStudent(student);
    setTimeout(() => {
      window.print();
      setPrintTargetStudent(null);
    }, 200);
  };

  const handlePrintBulk = () => {
    if (selectedStudentIds.size === 0) {
      if (showErrorNotification) showErrorNotification('No Students Selected', 'Please select at least one student to print.');
      else alert('Please select at least one student to print.');
      return;
    }
    setPrintTargetStudent(null);
    setTimeout(() => {
      window.print();
    }, 200);
  };

  // Download Single PDF using jsPDF + html2canvas
  const handleDownloadSinglePDF = async (student) => {
    try {
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:0;left:0;opacity:0.01;pointer-events:none;z-index:-9999;width:750px;background:#ffffff;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');
      const root = createRoot(container);

      await new Promise(resolve => {
        root.render(
          <div id="single-pdf-render-workspace" style={{ padding: '10px', background: '#ffffff' }}>
            <HallTicketCard
              student={student}
              examTitle={selectedExamTitle}
              academicYear={academicYear}
              timetable={examTimetable}
              orgData={orgData}
              brandingData={brandingData}
              examCenter={examCenter}
              reportingTime={effectiveReportingTime}
            />
          </div>
        );
        setTimeout(resolve, 400);
      });

      const captureEl = document.getElementById('single-pdf-render-workspace');
      const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);

      const filename = `${(student.name || 'Student').replace(/\s+/g, '_')}_${selectedExamTitle}_HallTicket.pdf`;
      pdf.save(filename);

      root.unmount();
      document.body.removeChild(container);

      if (showSuccessNotification) showSuccessNotification('Hall Ticket Downloaded', `Saved ${filename}`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      if (showErrorNotification) showErrorNotification('PDF Error', err.message);
    }
  };

  // Download Bulk PDF
  const handleDownloadBulkPDF = async () => {
    const targets = eligibleStudents.filter(s => selectedStudentIds.has(s.studentId || s.id));
    if (targets.length === 0) {
      if (showErrorNotification) showErrorNotification('No Students Selected', 'Please select at least one student to download.');
      else alert('Please select at least one student to download.');
      return;
    }

    setIsBulkGenerating(true);
    setBulkProgress({ current: 0, total: targets.length, msg: 'Initializing PDF document...' });

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:0;left:0;opacity:0.01;pointer-events:none;z-index:-9999;width:750px;background:#ffffff;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');

      for (let i = 0; i < targets.length; i++) {
        const student = targets[i];
        setBulkProgress({
          current: i + 1,
          total: targets.length,
          msg: `Generating Hall Ticket for ${student.name || student.studentName || 'Student'} (${i + 1}/${targets.length})...`
        });

        const studentWrapper = document.createElement('div');
        studentWrapper.id = `bulk-pdf-render-slot-${i}`;
        studentWrapper.style.cssText = 'padding: 10px; background: #ffffff;';
        container.appendChild(studentWrapper);

        const root = createRoot(studentWrapper);
        await new Promise(resolve => {
          root.render(
            <HallTicketCard
              student={student}
              examTitle={selectedExamTitle}
              academicYear={academicYear}
              timetable={examTimetable}
              orgData={orgData}
              brandingData={brandingData}
              examCenter={examCenter}
              reportingTime={effectiveReportingTime}
            />
          );
          setTimeout(resolve, 350);
        });

        const canvas = await html2canvas(studentWrapper, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);

        root.unmount();
        container.removeChild(studentWrapper);
      }

      document.body.removeChild(container);

      const batchFilename = `${selectedClass.replace(/\s+/g, '_')}_${selectedSection}_${selectedExamTitle}_HallTickets.pdf`;
      pdf.save(batchFilename);

      if (showSuccessNotification) {
        showSuccessNotification('Bulk Hall Tickets Generated', `Downloaded ${targets.length} hall tickets successfully.`);
      }
    } catch (err) {
      console.error('Bulk PDF Error:', err);
      if (showErrorNotification) showErrorNotification('Bulk PDF Error', err.message);
    } finally {
      setIsBulkGenerating(false);
      setBulkProgress({ current: 0, total: 0, msg: '' });
    }
  };

  // Determine students to display in the hidden print DOM
  const studentsToPrint = useMemo(() => {
    if (printTargetStudent) return [printTargetStudent];
    return eligibleStudents.filter(s => selectedStudentIds.has(s.studentId || s.id));
  }, [printTargetStudent, eligibleStudents, selectedStudentIds]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>

      {/* PRINT-ONLY CSS STYLESHEET */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            body * {
              visibility: hidden !important;
            }
            #hall-ticket-print-wrapper,
            #hall-ticket-print-wrapper *,
            .printable-hall-ticket,
            .printable-hall-ticket * {
              visibility: visible !important;
            }
            #hall-ticket-print-wrapper {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
            }
            .hall-ticket-card-page {
              page-break-after: always !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-shadow: none !important;
              border: 2px solid #0f172a !important;
              margin: 0 auto 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              min-height: 270mm !important;
            }
            .no-print {
              display: none !important;
              visibility: hidden !important;
            }
          }
        `
      }} />

      {/* 1. Header Toolbar & Breadcrumb */}
      <div className="no-print" style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '16px 20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {/* Title and Top Row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                background: '#eff6ff',
                color: '#1e3a8a',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '11.5px',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Ticket size={14} /> ADMIT CARDS
              </span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                Examination Hall Tickets
              </h3>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
              Generate, preview, and print official exam hall tickets for students by class and section.
            </p>
          </div>

          {/* Quick Actions */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handlePrintBulk}
              disabled={selectedStudentIds.size === 0 || isBulkGenerating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: selectedStudentIds.size > 0 ? '#1e3a8a' : '#cbd5e1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: selectedStudentIds.size > 0 ? 'pointer' : 'not-allowed',
                boxShadow: selectedStudentIds.size > 0 ? '0 2px 4px rgba(30,58,138,0.2)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              <Printer size={15} />
              <span>Print Selected ({selectedStudentIds.size})</span>
            </button>

            <button
              type="button"
              onClick={handleDownloadBulkPDF}
              disabled={selectedStudentIds.size === 0 || isBulkGenerating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#ffffff',
                color: selectedStudentIds.size > 0 ? '#1e3a8a' : '#94a3b8',
                border: '1.5px solid #cbd5e1',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: selectedStudentIds.size > 0 && !isBulkGenerating ? 'pointer' : 'not-allowed'
              }}
            >
              <Download size={15} />
              <span>Download PDF ({selectedStudentIds.size})</span>
            </button>
          </div>
        </div>

        {/* Filters Bar: Academic Year, Exam, Class, Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #f1f5f9'
        }}>
          {/* Step 1: Academic Year */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
              Academic Year
            </label>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#0f172a',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              {academicYearOptions.map(yr => (
                <option key={yr} value={yr}>{yr} Session</option>
              ))}
            </select>
          </div>

          {/* Step 2: Official Exam Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', marginBottom: '4px' }}>
              1. Select Examination
            </label>
            <select
              value={selectedExamTitle}
              onChange={(e) => setSelectedExamTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #93c5fd',
                fontSize: '13px',
                fontWeight: '800',
                color: '#1e3a8a',
                background: '#eff6ff',
                cursor: 'pointer'
              }}
            >
              {availableExams.length === 0 ? (
                <option value="">No Exams Scheduled</option>
              ) : (
                availableExams.map(ex => (
                  <option key={ex} value={ex}>{ex}</option>
                ))
              )}
            </select>
          </div>

          {/* Step 3: Class Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
              2. Class
            </label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedSection('ALL');
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#0f172a',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              {availableClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          {/* Step 4: Section Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
              3. Section
            </label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#0f172a',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All Sections</option>
              {availableSections.map(sec => (
                <option key={sec} value={sec}>Section {sec}</option>
              ))}
            </select>
          </div>

          {/* Exam Center (Printable override) */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
              Exam Center
            </label>
            <input
              type="text"
              value={examCenter}
              onChange={(e) => setExamCenter(e.target.value)}
              placeholder="e.g. Main Campus"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                color: '#0f172a',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Reporting Time */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
              Reporting Time
            </label>
            <input
              type="text"
              value={customReportingTime}
              onChange={(e) => setCustomReportingTime(e.target.value)}
              placeholder={autoReportingTime ? `${autoReportingTime} (15m prior)` : 'Leave empty if no fixed time'}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '7px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                color: '#0f172a',
                boxSizing: 'border-box'
              }}
            />
            <span style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px', display: 'block' }}>
              {autoReportingTime 
                ? `Derived: ${autoReportingTime} (15m prior to exam)` 
                : 'No exam start time found; no fabricated time.'}
            </span>
          </div>
        </div>
      </div>

      {/* 2. Bulk PDF Generation Progress Bar */}
      {isBulkGenerating && (
        <div className="no-print" style={{
          background: '#ffffff',
          borderRadius: '10px',
          padding: '14px 20px',
          border: '1.5px solid #93c5fd',
          boxShadow: '0 2px 8px rgba(30,58,138,0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e3a8a' }}>
            <span>{bulkProgress.msg}</span>
            <span>{bulkProgress.current} / {bulkProgress.total}</span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${(bulkProgress.current / Math.max(bulkProgress.total, 1)) * 100}%`,
              height: '100%',
              background: '#2563eb',
              transition: 'width 0.2s ease'
            }} />
          </div>
        </div>
      )}

      {/* 3. Exam Schedule Notice Card */}
      <div className="no-print" style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '14px 20px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpen size={16} color="#1e3a8a" />
            <span style={{ fontSize: '13.5px', fontWeight: '800', color: '#0f172a' }}>
              Scheduled Papers for {selectedExamTitle || 'Exam'} — {selectedClass} {selectedSection && selectedSection !== 'ALL' ? `(Sec ${selectedSection})` : ''}:
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: examTimetable.length > 0 ? '#16a34a' : '#dc2626' }}>
              {examTimetable.length > 0 ? `✓ ${examTimetable.length} Subjects Scheduled` : '⚠️ No timetable scheduled'}
            </span>
            {typeof onOpenExamSetup === 'function' && selectedExamTitle && (
              <button
                type="button"
                onClick={() => onOpenExamSetup({
                  examTitle: selectedExamTitle,
                  className: selectedClass,
                  section: selectedSection,
                  academicYear: academicYear
                })}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '11.5px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                title="Edit dates, start/end times and subjects for this exam"
              >
                📅 Edit Dates & Times
              </button>
            )}
          </div>
        </div>

        {examTimetable.length === 0 ? (
          <div style={{
            marginTop: '10px',
            padding: '12px 16px',
            borderRadius: '8px',
            background: '#fffbeb',
            border: '1px solid #fef3c7',
            fontSize: '12.5px',
            color: '#b45309'
          }}>
            No timetable has been announced for <strong>{selectedExamTitle || 'this exam'}</strong> in <strong>{selectedClass}</strong> yet. You can schedule it under the <strong>1. School Exams</strong> tab.
          </div>
        ) : (
          <>
            {/* Rule 4: Explicit notification if underlying schedule lacks individual start/end times */}
            {examTimetable.some(r => !r.startTime || r.startTime.trim() === '') && (
              <div style={{
                marginTop: '10px',
                marginBottom: '8px',
                padding: '10px 14px',
                borderRadius: '8px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                fontSize: '12px',
                color: '#1e3a8a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span style={{ fontSize: '15px' }}>ℹ️</span>
                  <span>
                    <strong>Official Schedule Notice:</strong> Underlying examination schedule contains date and subject data without individual paper start/end times. If individual times are added in <strong>Exam Setup</strong>, they will automatically appear on all Hall Tickets.
                  </span>
                </div>
                {typeof onOpenExamSetup === 'function' && (
                  <button
                    type="button"
                    onClick={() => onOpenExamSetup({
                      examTitle: selectedExamTitle,
                      className: selectedClass,
                      section: selectedSection,
                      academicYear: academicYear
                    })}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: '#1e3a8a',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 1px 3px rgba(30,58,138,0.2)'
                    }}
                  >
                    📅 Edit Exam Schedule & Timetable
                  </button>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
              {examTimetable.map((subj, sIdx) => {
                const timing = resolveExamTiming(subj);
                return (
                  <div
                    key={subj.testId || sIdx}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span style={{ fontWeight: '800', color: '#1e293b' }}>{subj.subjectName}</span>
                    <span style={{ color: '#64748b' }}>•</span>
                    <span style={{ color: '#0f172a', fontWeight: '600' }}>{formatDisplayDate(subj.testDate)}</span>
                    {timing !== '—' && (
                      <>
                        <span style={{ color: '#64748b' }}>•</span>
                        <span style={{ color: '#2563eb', fontWeight: '700' }}>{timing}</span>
                      </>
                    )}
                    <span style={{
                      background: '#eff6ff',
                      color: '#1e3a8a',
                      padding: '1px 5px',
                      borderRadius: '4px',
                      fontSize: '10.5px',
                      fontWeight: '700'
                    }}>
                      {subj.maxMarks}m
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* 4. Student Roster Workspace */}
      <div className="no-print" style={{
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {/* Roster Header Toolbar */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
              Candidate List ({eligibleStudents.length} Students)
            </span>
            <span style={{
              background: selectedStudentIds.size === eligibleStudents.length && eligibleStudents.length > 0 ? '#f0fdf4' : '#eff6ff',
              color: selectedStudentIds.size === eligibleStudents.length && eligibleStudents.length > 0 ? '#16a34a' : '#1e3a8a',
              border: '1px solid #cbd5e1',
              padding: '3px 9px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '700'
            }}>
              {selectedStudentIds.size} of {eligibleStudents.length} selected
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: '2px 6px'
                }}
              >
                Select All
              </button>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <button
                type="button"
                onClick={handleClearAll}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: '2px 6px'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Search box */}
          <div style={{ position: 'relative', width: '240px' }}>
            <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search candidate, roll no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 12px 6px 30px',
                borderRadius: '6px',
                border: '1.5px solid #cbd5e1',
                fontSize: '12.5px',
                color: '#0f172a',
                boxSizing: 'border-box'
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex'
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Student Table */}
        {loadingStudents ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
            <RefreshCw size={20} className="animate-spin" style={{ marginBottom: '8px' }} />
            <div>Loading student records...</div>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
            <User size={36} color="#cbd5e1" style={{ marginBottom: '8px' }} />
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#334155' }}>
              No students found
            </h4>
            <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#94a3b8' }}>
              {searchTerm ? `No candidate matches "${searchTerm}".` : `No students enrolled in ${selectedClass} ${selectedSection !== 'ALL' ? 'Sec ' + selectedSection : ''}.`}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                  <th style={{ padding: '10px 14px', width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.size === filteredStudents.length && filteredStudents.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) handleSelectAll();
                        else handleClearAll();
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '46px' }}>Photo</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Candidate Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '120px' }}>Roll Number</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '120px' }}>Admission No</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '110px' }}>Class & Sec</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '140px' }}>Parent/Guardian</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', width: '220px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((st) => {
                  const sId = st.studentId || st.id;
                  const isChecked = selectedStudentIds.has(sId);
                  const pUrl = resolveStudentPhotoUrl(st);

                  return (
                    <tr
                      key={sId}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: isChecked ? '#f8fafc' : '#ffffff',
                        transition: 'background 0.15s ease'
                      }}
                    >
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleStudent(sId)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>

                      <td style={{ padding: '8px 12px' }}>
                        {pUrl ? (
                          <img
                            src={pUrl}
                            alt=""
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1' }}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#eff6ff',
                            color: '#1e3a8a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '800',
                            fontSize: '12px'
                          }}>
                            {(st.name || st.studentName || 'S').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>

                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: '800', color: '#0f172a' }}>
                          {st.name || st.studentName || 'Student Name'}
                        </div>
                        {st.dob && (
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            DOB: {formatDisplayDate(st.dob || st.dateOfBirth)}
                          </div>
                        )}
                      </td>

                      <td style={{ padding: '10px 12px', fontWeight: '700', color: '#1e3a8a' }}>
                        {st.rollNo || st.rollNumber || '—'}
                      </td>

                      <td style={{ padding: '10px 12px', color: '#475569', fontWeight: '600' }}>
                        {st.admissionNo || st.studentId || '—'}
                      </td>

                      <td style={{ padding: '10px 12px', color: '#0f172a', fontWeight: '700' }}>
                        {selectedClass} {st.section ? `(${st.section})` : ''}
                      </td>

                      <td style={{ padding: '10px 12px', color: '#475569', fontSize: '12px' }}>
                        {st.fatherName || st.parentName || st.guardianName || '—'}
                      </td>

                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => setPreviewStudent(st)}
                            title="Preview Hall Ticket"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#ffffff',
                              color: '#1e3a8a',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              padding: '5px 10px',
                              fontSize: '11.5px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            <Eye size={13} /> Preview
                          </button>

                          <button
                            type="button"
                            onClick={() => handlePrintSingle(st)}
                            title="Print Hall Ticket"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#eff6ff',
                              color: '#1e3a8a',
                              border: '1px solid #bfdbfe',
                              borderRadius: '6px',
                              padding: '5px 10px',
                              fontSize: '11.5px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            <Printer size={13} /> Print
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownloadSinglePDF(st)}
                            title="Download PDF"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#ffffff',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              padding: '5px 9px',
                              fontSize: '11.5px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            <Download size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 5. Interactive Live Preview Modal */}
      {previewStudent && (
        <div className="no-print" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            width: '100%',
            maxWidth: '850px',
            maxHeight: '94vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc'
            }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                  🎫 Hall Ticket Preview — {previewStudent.name || previewStudent.studentName}
                </h4>
                <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                  {selectedClass} {previewStudent.section ? `(Sec ${previewStudent.section})` : ''} • Roll: {previewStudent.rollNo || previewStudent.admissionNo || '—'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => handlePrintSingle(previewStudent)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#1e3a8a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '7px',
                    padding: '7px 14px',
                    fontSize: '12.5px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  <Printer size={14} /> Print
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadSinglePDF(previewStudent)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#ffffff',
                    color: '#1e3a8a',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '7px',
                    padding: '7px 14px',
                    fontSize: '12.5px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  <Download size={14} /> PDF
                </button>

                <button
                  type="button"
                  onClick={() => setPreviewStudent(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body with Zoomed Printable Card */}
            <div style={{ padding: '20px', overflowY: 'auto', background: '#f1f5f9', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '720px' }}>
                <HallTicketCard
                  student={previewStudent}
                  examTitle={selectedExamTitle}
                  academicYear={academicYear}
                  timetable={examTimetable}
                  orgData={orgData}
                  brandingData={brandingData}
                  examCenter={examCenter}
                  reportingTime={effectiveReportingTime}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. HIDDEN PRINT WRAPPER FOR window.print() */}
      <div id="hall-ticket-print-wrapper" style={{ display: 'none' }}>
        {studentsToPrint.map((student, idx) => (
          <HallTicketCard
            key={student.studentId || student.id || idx}
            student={student}
            examTitle={selectedExamTitle}
            academicYear={academicYear}
            timetable={examTimetable}
            orgData={orgData}
            brandingData={brandingData}
            examCenter={examCenter}
            reportingTime={effectiveReportingTime}
          />
        ))}
      </div>

    </div>
  );
};

export default HallTicketGenerator;
